import get from 'lodash.get';

import { createLoadMoreMethod } from '../../../util/apollo';

/**
 * Accepts a source object and a `mappings` array and fetches records and
 * returns a normalized object that represents the available records for
 * selections for a custom print configuration.
 *
 * For usage with CustomPrintConfiguratorPhotoSelectorModal.
 *
 * @param {Object} options - The options.
 * @param {Object} options.source - The source object to extract records from.
 * @param {Array<Object>} options.mappings - The tab mappings in the form of {id, label, path, [mapper], [loader]}.
 * @param {Function} [options.fetchMore] - Optional fetchMore function if there's a need to create loadMore methods.
 * @returns {Array<Object>} - The candidate photo groups.
 */
export const createCustomPrintCandidatePhotoGroups = ({
  source,
  mappings,
  fetchMore,
}) => {
  if (!source) {
    return [];
  }

  return mappings
    .map(({ id, label, path, mapper = x => x, loader }) => {
      // Do not replace lodash.get here.
      const data = get(source, path, {});
      const loadMore =
        loader ||
        createLoadMoreMethod({
          source,
          recordsPath: path,
          offsetVariableLabel: `${id}Offset`,
          fetchMore,
        });

      return {
        id,
        label,
        total: data.total,
        photos: (data.records || []).map(mapper),
        loadMore,
      };
    })
    .filter(group => group.photos.length > 0);
};

/**
 * A more specialized version of `createCustomPrintCandidatePhotoGroups`.
 *
 * Takes in a PurchasedCustomPrint records and returns a normalized object
 * that represents the available photos for selections for a custom print
 * configuration.
 *
 * For usage with CustomPrintConfiguratorPhotoSelectorModal.
 *
 * @param {Object} purchasedCustomPrint - The purchased custom print.
 * @param {Object<Function>} [fetchMore] - Apollo's fetchMore function.
 * @returns {Array<Object>}
 */
export const getPurchasedCustomPrintCandidatePhotoGroups = (
  purchasedCustomPrint,
  fetchMore,
) => {
  return createCustomPrintCandidatePhotoGroups({
    source: purchasedCustomPrint,
    mappings: [
      {
        id: 'matches',
        label: 'Matches',
        path: 'selected_identity.matched_photos_extended',
        mapper: record => record.photo,
      },
      {
        id: 'favorites',
        label: 'Favorites',
        path: 'selected_identity.album_membership.favorited_photos',
      },
      {
        id: 'albumPhotos',
        label: 'All Photos',
        path: 'album.photos',
      },
    ],
    fetchMore,
  });
};
